import { FC, ReactNode, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import StatusBadge from '../common/status-badge'

type ServiceIntegrationCardProps = {
  title: string
  description: string
  icon: ReactNode
  iconColor?: string
  isConnected: boolean
  disabled?: boolean
  disabledMessage?: string
  setupContent: ReactNode
  manageContent?: ReactNode
}

const ServiceIntegrationCard: FC<ServiceIntegrationCardProps> = ({
  title,
  description,
  icon,
  iconColor,
  isConnected,
  disabled = false,
  disabledMessage = 'Contact Journey to enable',
  setupContent,
  manageContent,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const cardContent = (
    <Card className={`relative max-w-xl ${disabled && 'opacity-50'}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center flex-shrink-0">
              <div className={`${iconColor || 'text-foreground'}`}>{icon}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <CardTitle>{title}</CardTitle>
                {isConnected && <StatusBadge status="connected" />}
              </div>
              <CardDescription className={`${disabled ? 'text-muted-foreground/80' : ''}`}>
                {description}
              </CardDescription>
            </div>
          </div>
          <div className="flex-shrink-0 ml-4">
            {disabled ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                data-testid={`${title.toLowerCase()}-card-connect-button-disabled`}
              >
                Connect
              </Button>
            ) : (
              <Button
                size="sm"
                variant={'outline'}
                onClick={() => setIsModalOpen(true)}
                data-testid={`${title.toLowerCase()}-card-connect-button`}
              >
                {isConnected ? 'Manage' : 'Connect'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <>
      {disabled ? (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
            <TooltipContent>
              <p>{disabledMessage}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        cardContent
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`${iconColor || 'text-foreground'}`}>{icon}</div>
              {isConnected ? `Manage ${title}` : `Connect ${title}`}
            </DialogTitle>
            <DialogDescription>
              {isConnected
                ? `View and manage your ${title} integration settings.`
                : `Enter your ${title} credentials to start the integration.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">{isConnected ? manageContent || setupContent : setupContent}</div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ServiceIntegrationCard
